import * as React from 'react'
import { Select, Input, Button, Tooltip, message } from 'antd'
import { SelectValue } from 'antd/lib/select'
import { IBatchResponse, AuditBatchAPI } from '@getgreenline/homi-shared'
import { parseErrorMsg, filterOption } from '../../../../../../utilities/helpers'
import { AuditStore } from '../EditAuditStore'
import { observer } from 'mobx-react'
import { AuditProductModels } from '@getgreenline/audits'

interface Props {
  loading: boolean
  auditStore: AuditStore
  auditProduct: AuditProductModels.EditAuditProduct
  setIsAdding: (isAdding: boolean) => void
}

interface State {
  isBatchNameBeingAdded: boolean
  searchValue: string | undefined
  newBatchName: string | undefined
}

@observer
export class AuditBatchOptions extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      isBatchNameBeingAdded: false,
      searchValue: undefined,
      newBatchName: undefined,
    }
  }

  addAuditBatch = async (availableBatchOption: IBatchResponse) => {
    const { auditStore, auditProduct } = this.props

    try {
      const addedAuditBatch = await AuditBatchAPI.addAuditBatch(
        auditStore.company.id,
        auditStore.audit.id,
        {
          inventoryAuditProductId: auditProduct.id,
          batchId: availableBatchOption.id,
          expectedQuantity: availableBatchOption.quantityRemaining,
          enteredQuantity: null,
        },
      )

      // Add the selected batch to product object
      auditProduct.addBatch({
        id: addedAuditBatch.id!,
        inventoryAuditProductId: addedAuditBatch.inventoryAuditProductId,
        batchId: addedAuditBatch.batchId,
        batchNumber: availableBatchOption.batchNumber,
        quantity: addedAuditBatch.expectedQuantity,
        quantityRemaining: addedAuditBatch.expectedQuantity,
      })

      message.success(
        <>
          Lot <strong>{availableBatchOption.batchNumber}</strong> was successfully added
        </>,
      )
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error adding an lot number')
    }
  }

  createAuditBatchFromBatchOption = async (batchId: SelectValue) => {
    const availableBatchOption = this.unselectedBatchOptions.find((batch) => batch.id === batchId)

    if (!availableBatchOption) {
      return
    }

    await this.addAuditBatch(availableBatchOption)
  }

  createNewBatchAndAddToAuditProduct = async () => {
    const { auditStore, auditProduct, setIsAdding } = this.props

    const trimmedNewBatchName = (this.state.newBatchName || '').trim()

    if (!trimmedNewBatchName) {
      return message.error('Lot number is empty')
    }

    const duplicateBatchOption = auditStore.getMatchingBatchOption(
      auditProduct.productId,
      trimmedNewBatchName,
    )

    if (duplicateBatchOption) {
      return message.error('Lot number already exists')
    }

    setIsAdding(true)

    this.setState({ isBatchNameBeingAdded: true })

    const availableBatchOption = await auditStore.createNewBatchOption(
      auditProduct,
      trimmedNewBatchName,
    )

    await this.addAuditBatch(availableBatchOption)

    this.setState({ isBatchNameBeingAdded: false, newBatchName: undefined })

    setIsAdding(false)
  }

  get unselectedBatchOptions(): IBatchResponse[] {
    const { auditStore, auditProduct } = this.props

    const selectedBatchIds = auditProduct.batches.map((selectedBatch) => selectedBatch.batchId)

    const allBatchOptions = auditStore.getAllBatchOptionsForProduct(auditProduct.productId) || []

    return allBatchOptions.filter((batchOption) => !selectedBatchIds.includes(batchOption.id))
  }

  render() {
    const { isBatchNameBeingAdded, searchValue, newBatchName } = this.state

    return (
      <div className='d-flex mt-2'>
        <Select
          showArrow
          showSearch
          loading={this.props.loading}
          placeholder={this.props.loading ? 'Loading lot options..' : 'Select a lot'}
          value={searchValue}
          dropdownMatchSelectWidth={false}
          onChange={this.createAuditBatchFromBatchOption}
          onSelect={() => this.setState({ searchValue: undefined })} // Clear select value after select
          filterOption={filterOption}
          style={{ width: 210 }}
          dropdownStyle={{ maxWidth: 400 }}
        >
          {this.unselectedBatchOptions.map((batch) => (
            <Select.Option key={batch.id} value={batch.id}>
              {batch.batchNumber}
            </Select.Option>
          ))}
        </Select>

        {isBatchNameBeingAdded ? (
          <>
            <Input
              className='ml-2 mr-2'
              placeholder='Add a new lot'
              value={newBatchName}
              onChange={(e) => this.setState({ newBatchName: e.target.value })}
              style={{ width: 200 }}
              onPressEnter={this.createNewBatchAndAddToAuditProduct}
            />
            <Button onClick={this.createNewBatchAndAddToAuditProduct}>Add</Button>
          </>
        ) : (
          <Tooltip title='Add a missing lot number'>
            <div>
              <Button
                className='ml-2'
                onClick={() => this.setState({ isBatchNameBeingAdded: true })}
              >
                Add a new lot
              </Button>
            </div>
          </Tooltip>
        )}
      </div>
    )
  }
}
